import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '../store';
import type { Finding, Severity, TajweedRule, RuleScorecard, RuleScore, RuleStatus, EvaluationStatus } from '../types';
import { fetchExplanationAudio, fetchFindingAudio, getSheikhWordAudioUrl } from '../api/client';
import { useWordPractice } from '../hooks/useWordPractice';
import { clearEvaluation } from '../store/slices/evaluationSlice';
import { reset as resetRecitation } from '../store/slices/recitationSlice';
import clsx from 'clsx';

const BOGUS_STATUSES: EvaluationStatus[] = [
  'silence',
  'humming',
  'wrong_language',
  'wrong_ayah',
  'unintelligible',
];

function isBogusEvaluationStatus(status?: string): status is EvaluationStatus {
  return !!status && (BOGUS_STATUSES as string[]).includes(status);
}

const RULE_LABEL: Record<TajweedRule, string> = {
  madd: 'Madd (Elongation)',
  ghunnah: 'Ghunnah (Nasalization)',
  idgham: 'Idgham (Assimilation)',
  ikhfa: 'Ikhfa (Concealment)',
  iqlab: 'Iqlab (Conversion)',
  qalqalah: 'Qalqalah (Echo)',
  tafkheem_tarqeeq: 'Tafkheem/Tarqeeq',
  waqf_ibtida: 'Waqf/Ibtida (Stopping)',
  makhraj: 'Makhraj (Articulation)',
  sifat: 'Sifat (Letter Quality)',
  tajweed_rule: 'Tajweed Rule',
  timing: 'Timing',
  pronunciation: 'Pronunciation',
};

const SEVERITY_STYLES: Record<Severity, { border: string; bg: string; badge: string; label: string }> = {
  critical: {
    border: 'border-red-400/40 dark:border-red-500/40',
    bg: 'bg-red-50 dark:bg-red-500/5',
    badge: 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300',
    label: 'Critical',
  },
  major: {
    border: 'border-orange-400/40 dark:border-orange-500/40',
    bg: 'bg-orange-50 dark:bg-orange-500/5',
    badge: 'bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-300',
    label: 'Major',
  },
  minor: {
    border: 'border-yellow-400/40 dark:border-yellow-500/40',
    bg: 'bg-yellow-50 dark:bg-yellow-500/5',
    badge: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-500/20 dark:text-yellow-300',
    label: 'Minor',
  },
};

// --- ScoreRing ---

const ScoreRing: React.FC<{ score: number; confidence: number }> = ({ score, confidence }) => {
  const radius = 54;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (score / 100) * circumference;

  const color =
    score >= 80 ? '#10b981' : score >= 60 ? '#eab308' : '#ef4444';

  return (
    <div className="flex flex-col items-center">
      <div className="relative w-36 h-36">
        <svg className="w-full h-full -rotate-90" viewBox="0 0 120 120">
          <circle
            cx="60"
            cy="60"
            r={radius}
            fill="none"
            className="stroke-gray-200 dark:stroke-gray-800"
            strokeWidth="8"
          />
          <circle
            cx="60"
            cy="60"
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            className="transition-all duration-1000 ease-out"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-3xl font-bold" style={{ color }}>
            {Math.round(score)}
          </span>
          <span className="text-xs text-gray-500">/ 100</span>
        </div>
      </div>
      <p className="text-xs text-gray-500 mt-2">
        Confidence: {Math.round(confidence * 100)}%
      </p>
    </div>
  );
};

// --- FindingCard (with inline practice) ---

const FindingCard: React.FC<{
  finding: Finding;
  index: number;
  surah?: number;
  ayah?: number;
  language?: string;
  prefetchedAudioUrl?: string | null;
}> = ({ finding, index, surah = 1, ayah = 1, language = 'en', prefetchedAudioUrl }) => {
  const styles = SEVERITY_STYLES[finding.severity];

  // Explain audio (teacher explanation of the error)
  const [explainPlaying, setExplainPlaying] = useState(false);
  const explainAudioRef = useRef<HTMLAudioElement | null>(null);
  const explainUrl = prefetchedAudioUrl;
  const explainLoading = explainUrl === undefined;

  // Sheikh word audio (the correct pronunciation)
  const [sheikhPlaying, setSheikhPlaying] = useState(false);
  const sheikhAudioRef = useRef<HTMLAudioElement | null>(null);

  // Inline practice state
  const [practiceOpen, setPracticeOpen] = useState(false);
  const word = finding.word || '';
  // Difficulty is set globally on PracticePage. Read from localStorage at submit time
  // (within useWordPractice) so changes from the global picker apply immediately.
  // We track a state that re-reads on focus/storage events so toggling the global
  // picker reflects here too.
  const [difficulty, setDifficulty] = useState<'easy' | 'normal' | 'strict'>(() => {
    const stored = typeof localStorage !== 'undefined' ? localStorage.getItem('tajweed_practice_difficulty') : null;
    return stored === 'normal' || stored === 'strict' ? stored : 'easy';
  });
  useEffect(() => {
    const onStorage = () => {
      const stored = localStorage.getItem('tajweed_practice_difficulty');
      setDifficulty(stored === 'normal' || stored === 'strict' ? stored : 'easy');
    };
    window.addEventListener('storage', onStorage);
    // Same-tab updates don't fire 'storage' — listen for our custom event too
    window.addEventListener('tajweed:difficulty-changed', onStorage);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('tajweed:difficulty-changed', onStorage);
    };
  }, []);
  const practice = useWordPractice({
    surah, ayah, word, repairHint: finding.repairHint || '', language, difficulty,
  });

  const handlePlayExplain = useCallback(() => {
    if (explainPlaying && explainAudioRef.current) {
      explainAudioRef.current.pause();
      setExplainPlaying(false);
      return;
    }
    if (explainUrl) {
      const audio = new Audio(explainUrl);
      explainAudioRef.current = audio;
      audio.onended = () => setExplainPlaying(false);
      audio.play();
      setExplainPlaying(true);
    }
  }, [explainUrl, explainPlaying]);

  const playSheikh = useCallback(() => {
    if (!word) return;
    if (sheikhAudioRef.current) sheikhAudioRef.current.pause();
    const url = getSheikhWordAudioUrl(surah, ayah, word);
    const audio = new Audio(url);
    sheikhAudioRef.current = audio;
    audio.onended = () => setSheikhPlaying(false);
    audio.play().catch(() => setSheikhPlaying(false));
    setSheikhPlaying(true);
  }, [surah, ayah, word]);

  const handleMic = () => {
    if (practice.recording) practice.stopRecording();
    else if (!practice.evaluating) {
      // Pause sheikh if playing
      if (sheikhAudioRef.current) {
        sheikhAudioRef.current.pause();
        setSheikhPlaying(false);
      }
      practice.startRecording();
    }
  };

  const togglePractice = () => {
    if (practiceOpen) {
      // Closing — clean up
      if (practice.recording) practice.stopRecording();
      setPracticeOpen(false);
    } else {
      setPracticeOpen(true);
      // Auto-play sheikh when opening
      setTimeout(() => playSheikh(), 200);
    }
  };

  const lastAttempt = practice.attempts[practice.attempts.length - 1];
  const nailedIt = lastAttempt?.verdict === 'perfect';

  return (
    <div className={clsx('rounded-xl border p-4 transition-all', styles.border, styles.bg, practiceOpen && 'ring-2 ring-emerald-400/40 dark:ring-emerald-500/30')}>
      {/* Header */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">
            #{index + 1} {RULE_LABEL[finding.rule] || finding.rule}
            {word && (
              <span className="ml-1.5 font-arabic text-base text-gray-800 dark:text-gray-100">
                {word}
              </span>
            )}
          </span>
          <span className={clsx('badge text-xs', styles.badge)}>{styles.label}</span>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            onClick={handlePlayExplain}
            disabled={explainLoading}
            className={clsx(
              'inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-all',
              explainPlaying
                ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300'
                : 'bg-gray-200 dark:bg-gray-700 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 text-gray-600 dark:text-gray-300'
            )}
            title="Hear the teacher explain this error"
          >
            {explainLoading ? (
              <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            ) : explainPlaying ? (
              <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 24 24">
                <rect x="6" y="4" width="4" height="16" rx="1" />
                <rect x="14" y="4" width="4" height="16" rx="1" />
              </svg>
            ) : (
              <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            )}
            {explainLoading ? '...' : explainPlaying ? 'Pause' : 'Explain'}
          </button>
        </div>
      </div>

      {/* What happened */}
      <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">{finding.explanationForUser}</p>

      {/* Repair hint */}
      {finding.repairHint && (
        <div className="bg-gray-100/70 dark:bg-gray-800/50 rounded-lg px-3 py-2 mb-3">
          <p className="text-xs text-emerald-600 dark:text-emerald-400 font-medium mb-0.5">How to fix it</p>
          <p className="text-sm text-gray-600 dark:text-gray-300">{finding.repairHint}</p>
        </div>
      )}

      {/* Practice CTA — collapsed view */}
      {word && !practiceOpen && (
        <button
          onClick={togglePractice}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold bg-gradient-to-br from-emerald-500 to-emerald-700 hover:from-emerald-400 hover:to-emerald-600 text-white shadow-md shadow-emerald-500/25 hover:shadow-lg hover:shadow-emerald-500/30 transition-all"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4M12 15a3 3 0 003-3V5a3 3 0 00-6 0v7a3 3 0 003 3z" />
          </svg>
          Practice this word
          <svg className="h-3 w-3 opacity-70" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      )}

      {/* Practice expanded — inline */}
      {word && practiceOpen && (
        <div className="mt-3 rounded-xl border border-emerald-200 dark:border-emerald-500/30 bg-white/80 dark:bg-gray-900/40 p-4 space-y-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <p className="text-xs uppercase tracking-wider font-medium text-emerald-700 dark:text-emerald-300">
                Practice
              </p>
              <span
                className={clsx(
                  'text-[10px] uppercase tracking-wider font-medium px-1.5 py-0.5 rounded',
                  difficulty === 'easy' && 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400',
                  difficulty === 'normal' && 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
                  difficulty === 'strict' && 'bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-400',
                )}
                title="Change difficulty in the top bar"
              >
                {difficulty}
              </span>
            </div>
            <button
              onClick={togglePractice}
              className="text-xs text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            >
              Close
            </button>
          </div>

          {/* Sheikh demo — large word + play */}
          <div className="flex flex-col items-center gap-2 py-2">
            <button
              onClick={playSheikh}
              className="font-arabic text-3xl text-gray-900 dark:text-white hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors"
              style={{ direction: 'rtl' }}
            >
              {word}
            </button>
            <button
              onClick={playSheikh}
              className={clsx(
                'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-all',
                sheikhPlaying
                  ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300'
                  : 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-500/20'
              )}
            >
              {sheikhPlaying ? (
                <>
                  <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 24 24"><rect x="6" y="5" width="4" height="14" rx="1" /><rect x="14" y="5" width="4" height="14" rx="1" /></svg>
                  Playing
                </>
              ) : (
                <>
                  <svg className="h-3 w-3 ml-0.5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                  Listen to sheikh
                </>
              )}
            </button>
          </div>

          {/* Mic */}
          <div className="flex flex-col items-center gap-3 py-2">
            <div className="relative flex items-center justify-center w-24 h-24">
              {practice.recording && (
                <>
                  <span
                    className="absolute rounded-full bg-red-400/20 transition-all duration-150"
                    style={{ width: `${72 + practice.inputLevel * 30}px`, height: `${72 + practice.inputLevel * 30}px` }}
                  />
                  <span
                    className="absolute rounded-full bg-red-400/10 transition-all duration-200"
                    style={{ width: `${88 + practice.inputLevel * 40}px`, height: `${88 + practice.inputLevel * 40}px` }}
                  />
                </>
              )}
              {practice.evaluating && (
                <span className="absolute w-20 h-20 rounded-full border-2 border-emerald-400/40 animate-ping" />
              )}
              <button
                onClick={handleMic}
                disabled={practice.evaluating}
                className={clsx(
                  'relative z-10 w-16 h-16 rounded-full flex items-center justify-center transition-all duration-200 focus:outline-none focus:ring-4',
                  practice.recording
                    ? 'bg-gradient-to-br from-red-500 to-red-700 text-white focus:ring-red-500/50 shadow-lg shadow-red-500/30'
                    : practice.evaluating
                    ? 'bg-gradient-to-br from-emerald-500/60 to-emerald-700/60 text-white/80 cursor-wait'
                    : 'bg-gradient-to-br from-emerald-500 to-emerald-700 hover:from-emerald-400 hover:to-emerald-600 text-white focus:ring-emerald-500/50 shadow-lg shadow-emerald-500/25'
                )}
                style={practice.recording ? { transform: `scale(${1 + practice.inputLevel * 0.08})` } : undefined}
              >
                {practice.evaluating ? (
                  <svg className="animate-spin h-6 w-6" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                ) : practice.recording ? (
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24"><rect x="6" y="6" width="12" height="12" rx="2" /></svg>
                ) : (
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4M12 15a3 3 0 003-3V5a3 3 0 00-6 0v7a3 3 0 003 3z" />
                  </svg>
                )}
              </button>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {practice.evaluating
                ? 'Listening...'
                : practice.recording
                ? 'Tap to stop'
                : nailedIt
                ? '✓ Got it. Try again or close.'
                : practice.attempts.length > 0
                ? `Tap to try again (attempt ${practice.attempts.length + 1})`
                : 'Tap and say the word'}
            </p>
          </div>

          {/* Attempts feed (latest first) */}
          {practice.attempts.length > 0 && (
            <div className="space-y-2 pt-2 border-t border-gray-200 dark:border-gray-700/50">
              {[...practice.attempts].reverse().map((att, idx) => {
                const realIdx = practice.attempts.length - 1 - idx;
                const isBogus =
                  att.status === 'silence' ||
                  att.status === 'humming' ||
                  att.status === 'wrong_language' ||
                  att.status === 'wrong_word' ||
                  att.status === 'unintelligible';
                const bogusLabel =
                  att.status === 'silence' ? "↻ Didn't catch that"
                  : att.status === 'humming' ? '↻ Heard humming'
                  : att.status === 'wrong_language' ? '↻ Different language'
                  : att.status === 'wrong_word' ? '↻ Different word'
                  : att.status === 'unintelligible' ? '↻ Unclear'
                  : null;
                return (
                  <div
                    key={realIdx}
                    className={clsx(
                      'rounded-lg border px-3 py-2 text-xs',
                      isBogus
                        ? 'border-amber-300 dark:border-amber-700/60 bg-amber-50/70 dark:bg-amber-900/20'
                        : att.verdict === 'perfect'
                        ? 'border-emerald-300 dark:border-emerald-700/60 bg-emerald-50/70 dark:bg-emerald-900/20'
                        : att.verdict === 'close'
                        ? 'border-amber-300 dark:border-amber-700/60 bg-amber-50/70 dark:bg-amber-900/20'
                        : 'border-red-300 dark:border-red-700/60 bg-red-50/70 dark:bg-red-900/20'
                    )}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-gray-700 dark:text-gray-200">
                        Attempt #{realIdx + 1} —{' '}
                        {bogusLabel
                          ? bogusLabel
                          : att.verdict === 'perfect'
                          ? '✓ Perfect'
                          : att.verdict === 'close'
                          ? '~ Close'
                          : '✗ Off'}
                      </span>
                      {!isBogus && (
                        <span
                          className={clsx(
                            'font-semibold',
                            att.verdict === 'perfect'
                              ? 'text-emerald-700 dark:text-emerald-300'
                              : att.verdict === 'close'
                              ? 'text-amber-700 dark:text-amber-300'
                              : 'text-red-700 dark:text-red-300'
                          )}
                        >
                          {att.matchPct}%
                        </span>
                      )}
                    </div>
                    <p className="text-gray-700 dark:text-gray-200 mb-1.5">{att.feedback}</p>
                    {isBogus && att.heard && (
                      <p className="text-[11px] italic text-gray-500 dark:text-gray-400 mb-1.5">
                        We heard: <span className="font-arabic">{att.heard}</span>
                      </p>
                    )}
                    <button
                      onClick={() => new Audio(att.audioUrl).play()}
                      className="inline-flex items-center gap-1 text-[11px] text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                    >
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                      Hear your attempt
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Evidence (only shown when practice is collapsed to keep card tight) */}
      {!practiceOpen && finding.evidence && (finding.evidence.expected || finding.evidence.observed) && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-gray-400 dark:text-gray-500 mt-3 pt-3 border-t border-gray-200/60 dark:border-gray-700/40">
          <span>{Math.round(finding.confidence01 * 100)}% confidence</span>
          {finding.evidence.durationMs != null && (
            <span>Duration: {finding.evidence.durationMs}ms</span>
          )}
        </div>
      )}
    </div>
  );
};

// --- Rule Scorecard ---

const RULE_ARABIC: Record<string, string> = {
  madd: 'مَدّ',
  ghunnah: 'غُنَّة',
  idgham: 'إِدْغَام',
  ikhfa: 'إِخْفَاء',
  iqlab: 'إِقْلَاب',
  qalqalah: 'قَلْقَلَة',
};

const STATUS_CONFIG: Record<RuleStatus, { icon: string; color: string; bg: string; border: string; label: string }> = {
  good: {
    icon: '✓',
    color: 'text-emerald-600 dark:text-emerald-400',
    bg: 'bg-emerald-50 dark:bg-emerald-500/10',
    border: 'border-emerald-200 dark:border-emerald-500/20',
    label: 'Good',
  },
  weak: {
    icon: '~',
    color: 'text-amber-600 dark:text-amber-400',
    bg: 'bg-amber-50 dark:bg-amber-500/10',
    border: 'border-amber-200 dark:border-amber-500/20',
    label: 'Needs Work',
  },
  missed: {
    icon: '✗',
    color: 'text-red-500 dark:text-red-400',
    bg: 'bg-red-50 dark:bg-red-500/10',
    border: 'border-red-200 dark:border-red-500/20',
    label: 'Missed',
  },
  not_applicable: {
    icon: '—',
    color: 'text-gray-400 dark:text-gray-600',
    bg: 'bg-gray-50 dark:bg-gray-800/50',
    border: 'border-gray-100 dark:border-gray-800',
    label: 'N/A',
  },
};

const RuleScorecardPanel: React.FC<{ scorecard: RuleScorecard }> = ({ scorecard }) => {
  const applicableRules = scorecard.rules.filter((r) => r.status !== 'not_applicable');
  const goodCount = applicableRules.filter((r) => r.status === 'good').length;

  if (applicableRules.length === 0) return null;

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200">Rule Scorecard</h3>
        <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 rounded-full px-2.5 py-1">
          {scorecard.summary}
        </span>
      </div>

      {/* Summary bar */}
      <div className="flex gap-1 h-2 rounded-full overflow-hidden mb-5">
        {applicableRules.map((r, i) => {
          const cfg = STATUS_CONFIG[r.status];
          return (
            <div
              key={i}
              className={clsx(
                'flex-1 transition-all',
                r.status === 'good' ? 'bg-emerald-400' : r.status === 'weak' ? 'bg-amber-400' : 'bg-red-400'
              )}
            />
          );
        })}
      </div>

      {/* Rule rows */}
      <div className="space-y-2">
        {scorecard.rules.map((rule) => {
          const cfg = STATUS_CONFIG[rule.status];
          const arabic = RULE_ARABIC[rule.rule] || '';
          const label = RULE_LABEL[rule.rule as TajweedRule] || rule.rule;

          return (
            <div key={rule.rule} className={clsx('rounded-lg border px-3 py-2.5', cfg.border, cfg.bg)}>
              <div className="flex items-center gap-3">
                <span className={clsx('text-base font-bold w-5 text-center', cfg.color)}>{cfg.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-arabic text-sm text-gray-600 dark:text-gray-400">{arabic}</span>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-200">{label}</span>
                    <span className={clsx('text-xs font-medium px-1.5 py-0.5 rounded', cfg.color, cfg.bg)}>{cfg.label}</span>
                  </div>
                  {rule.details.length > 0 && (
                    <div className="mt-1.5 space-y-1">
                      {rule.details.map((d, i) => {
                        const dCfg = STATUS_CONFIG[d.status];
                        const pct = Math.round(d.ratio * 100);
                        return (
                          <div key={i} className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                            <span className={clsx('font-bold', dCfg.color)}>{STATUS_CONFIG[d.status].icon}</span>
                            <span className="font-arabic text-sm text-gray-600 dark:text-gray-300" dir="rtl">{d.word}</span>
                            <span className="tabular-nums">{d.studentMs}ms</span>
                            <span className="text-gray-300 dark:text-gray-600">vs</span>
                            <span className="tabular-nums">{d.sheikhMs}ms</span>
                            {/* Progress bar */}
                            <div className="flex-1 max-w-[80px] h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                              <div
                                className={clsx(
                                  'h-full rounded-full transition-all',
                                  d.status === 'good' ? 'bg-emerald-400' : d.status === 'weak' ? 'bg-amber-400' : 'bg-red-400'
                                )}
                                style={{ width: `${Math.min(pct, 100)}%` }}
                              />
                            </div>
                            <span className={clsx('tabular-nums font-medium', dCfg.color)}>{pct}%</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// --- FeedbackPanel ---

function handleExportPDF(evaluation: import('../types').TajweedEvaluation) {
  const { meta, overall, findings } = evaluation;
  const lines = [
    'TAJWEED TUTOR — EVALUATION REPORT',
    '='.repeat(40),
    '',
    `Surah: ${meta.surah}  Ayah: ${meta.ayah}`,
    `Level: ${meta.userLevel}`,
    `Score: ${Math.round(overall.score0100)} / 100`,
    `Confidence: ${Math.round(overall.confidence01 * 100)}%`,
    '',
    'FINDINGS',
    '-'.repeat(20),
  ];

  if (findings.length === 0) {
    lines.push('No issues found — great recitation!');
  } else {
    findings.forEach((f, i) => {
      lines.push(`${i + 1}. [${f.severity.toUpperCase()}] ${f.rule}`);
      lines.push(`   ${f.explanationForUser}`);
      if (f.repairHint) lines.push(`   Hint: ${f.repairHint}`);
      lines.push('');
    });
  }

  lines.push('', '— Generated by Tajweed Tutor');

  const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `tajweed-report-${meta.surah}-${meta.ayah}.txt`;
  a.click();
  URL.revokeObjectURL(url);
}

const FeedbackPanel: React.FC<{ language?: string }> = ({ language = 'en' }) => {
  const dispatch = useDispatch<AppDispatch>();
  const evaluation = useSelector((state: RootState) => state.evaluation.current);
  const loading = useSelector((state: RootState) => state.evaluation.loading);
  // Layer 2: student's recording, kept in Redux post-eval. Used by /explain-finding
  // to splice the user's own attempt of the word alongside the sheikh's correct version.
  const studentAudioBlob = useSelector((state: RootState) => state.recitation.audioBlob);

  const handleTryAgain = useCallback(() => {
    dispatch(clearEvaluation());
    dispatch(resetRecitation());
  }, [dispatch]);

  const [explainUrl, setExplainUrl] = useState<string | null>(null);
  const [explainLoading, setExplainLoading] = useState(false);
  const [explainPlaying, setExplainPlaying] = useState(false);
  const explainAudioRef = useRef<HTMLAudioElement | null>(null);

  // Shared abort controller for all background prefetches
  const prefetchAbortRef = useRef<AbortController | null>(null);

  // Listen for "abort prefetches" event (fired when practice modal starts recording)
  useEffect(() => {
    const handler = () => {
      if (prefetchAbortRef.current) {
        prefetchAbortRef.current.abort();
        prefetchAbortRef.current = null;
      }
    };
    window.addEventListener('tajweed:abort-prefetches', handler);
    return () => window.removeEventListener('tajweed:abort-prefetches', handler);
  }, []);

  // New evaluation → fresh abort controller for the new prefetches
  useEffect(() => {
    prefetchAbortRef.current = new AbortController();
  }, [evaluation]);

  // Fetch voice explanation
  useEffect(() => {
    if (!evaluation) {
      setExplainUrl(null);
      return;
    }
    // Skip prefetches when audio was bogus — no findings to explain
    if (isBogusEvaluationStatus(evaluation.status)) {
      setExplainUrl(null);
      setExplainLoading(false);
      return;
    }
    let cancelled = false;
    setExplainLoading(true);
    const evalAny = evaluation as unknown as Record<string, unknown>;
    const tajweed = (evalAny.tajweedAnalysis || evalAny.tajweed_analysis || {}) as Record<string, unknown>;
    const praise = (tajweed.praise as string[]) || [];

    if (!prefetchAbortRef.current) prefetchAbortRef.current = new AbortController();
    fetchExplanationAudio(
      evaluation.findings as unknown as Record<string, unknown>[],
      praise,
      evaluation.overall.score0100,
      language,
      prefetchAbortRef.current.signal,
    )
      .then((url) => { if (!cancelled) setExplainUrl(url); })
      .catch(() => { if (!cancelled) setExplainUrl(null); })
      .finally(() => { if (!cancelled) setExplainLoading(false); });
    return () => { cancelled = true; };
  }, [evaluation, language]);

  // Prefetch all finding explanation audio in parallel
  const [findingAudioUrls, setFindingAudioUrls] = useState<Record<number, string | null>>({});

  useEffect(() => {
    if (!evaluation || evaluation.findings.length === 0) {
      setFindingAudioUrls({});
      return;
    }
    // Skip prefetches for bogus audio
    if (isBogusEvaluationStatus(evaluation.status)) {
      setFindingAudioUrls({});
      return;
    }
    let cancelled = false;
    const { meta: evalMeta, findings: evalFindings } = evaluation;

    if (!prefetchAbortRef.current) prefetchAbortRef.current = new AbortController();
    const signal = prefetchAbortRef.current.signal;

    // Layer 2: pull the student's recording + per-word Whisper timings so the
    // backend can splice "you said it like ‹word›, sheikh says it like «word»".
    // If anything's missing the request gracefully degrades to today's behavior.
    const wordTimings = evaluation.wordTimings || null;

    evalFindings.forEach((finding, idx) => {
      fetchFindingAudio(
        finding as unknown as Record<string, unknown>,
        evalMeta.surah,
        evalMeta.ayah,
        language,
        signal,
        studentAudioBlob,
        wordTimings,
      )
        .then((url) => { if (!cancelled) setFindingAudioUrls((prev) => ({ ...prev, [idx]: url })); })
        .catch(() => { if (!cancelled) setFindingAudioUrls((prev) => ({ ...prev, [idx]: null })); });
    });

    return () => { cancelled = true; };
  }, [evaluation, language, studentAudioBlob]);

  const handlePlayExplanation = () => {
    if (!explainUrl) return;
    if (explainPlaying && explainAudioRef.current) {
      explainAudioRef.current.pause();
      setExplainPlaying(false);
      return;
    }
    const audio = new Audio(explainUrl);
    explainAudioRef.current = audio;
    audio.onended = () => setExplainPlaying(false);
    audio.play();
    setExplainPlaying(true);
  };

  if (loading) {
    return (
      <div className="card flex flex-col items-center justify-center py-12 gap-3">
        <svg className="animate-spin h-8 w-8 text-emerald-500" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
        <p className="text-gray-500 dark:text-gray-400 text-sm">Analyzing your recitation...</p>
      </div>
    );
  }

  if (!evaluation) return null;

  // ── "We didn't catch a recitation" view ──
  if (isBogusEvaluationStatus(evaluation.status)) {
    const status = evaluation.status as EvaluationStatus;
    const heard =
      (evaluation as unknown as { tajweedAnalysis?: { summary?: string } }).tajweedAnalysis?.summary ||
      '';

    const COPY: Record<string, { title: string; subtitle: string }> = {
      silence: {
        title: "We didn't catch anything",
        subtitle: "Make sure your microphone is on and you're speaking close enough — then try again.",
      },
      humming: {
        title: 'We heard humming, not recitation',
        subtitle: 'Recite the ayah out loud with clear words — not just sounds. Then try again.',
      },
      wrong_language: {
        title: "That didn't sound like Qur'anic Arabic",
        subtitle: 'Recite the ayah in Arabic and try again.',
      },
      wrong_ayah: {
        title: 'That sounded like a different ayah',
        subtitle: "Try this exact ayah again — it's the one shown above.",
      },
      unintelligible: {
        title: "We couldn't make out the words",
        subtitle: 'Slow down and recite each word clearly. Then try again.',
      },
    };
    const copy = COPY[status] || COPY.silence;

    return (
      <div className="card relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-400 via-amber-500 to-orange-500" />
        <div className="flex flex-col items-center text-center py-6 px-4 gap-4">
          <div className="flex items-center justify-center w-16 h-16 rounded-full bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400">
            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 3l18 18" />
            </svg>
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-1">
              {copy.title}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm">
              {copy.subtitle}
            </p>
          </div>
          {heard && (
            <p className="text-xs italic text-gray-400 dark:text-gray-500 max-w-md">
              {heard}
            </p>
          )}
          <button
            onClick={handleTryAgain}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold bg-gradient-to-br from-emerald-500 to-emerald-700 hover:from-emerald-400 hover:to-emerald-600 text-white shadow-md shadow-emerald-500/25 transition-all"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4M12 15a3 3 0 003-3V5a3 3 0 00-6 0v7a3 3 0 003 3z" />
            </svg>
            Try again
          </button>
        </div>
      </div>
    );
  }

  const { meta, overall, findings, uncertainty, ruleScorecard } = evaluation;

  return (
    <div className="space-y-6">
      {/* Score + Uncertainty */}
      <div className="card">
        <div className="flex flex-col sm:flex-row items-center gap-6">
          <ScoreRing score={overall.score0100} confidence={overall.confidence01} />
          <div className="flex-1 text-center sm:text-left">
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-1">Your Score</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
              {overall.score0100 >= 80
                ? 'Excellent recitation! Keep up the great work.'
                : overall.score0100 >= 60
                ? 'Good effort. Review the findings below to improve.'
                : 'Keep practicing. Focus on the issues highlighted below.'}
            </p>
            <div className="flex flex-wrap items-center gap-3 justify-center sm:justify-start">
              <button
                onClick={handlePlayExplanation}
                disabled={explainLoading || !explainUrl}
                className={clsx(
                  'inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all',
                  explainPlaying
                    ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300'
                    : explainUrl
                    ? 'bg-emerald-600 hover:bg-emerald-500 text-white'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
                )}
              >
                {explainLoading ? (
                  <>
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Preparing feedback...
                  </>
                ) : explainPlaying ? (
                  <>
                    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                      <rect x="6" y="4" width="4" height="16" rx="1" />
                      <rect x="14" y="4" width="4" height="16" rx="1" />
                    </svg>
                    Pause Teacher
                  </>
                ) : (
                  <>
                    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                    Listen to Feedback
                  </>
                )}
              </button>
              <button
                onClick={() => handleExportPDF(evaluation)}
                className="inline-flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                </svg>
                Export Report
              </button>
            </div>
          </div>
        </div>

        {uncertainty && uncertainty.flags.length > 0 && (
          <div className="mt-4 bg-amber-50 dark:bg-amber-500/10 border border-amber-300 dark:border-amber-500/30 rounded-xl px-4 py-3">
            <p className="text-sm text-amber-700 dark:text-amber-300">{uncertainty.message}</p>
            <div className="flex gap-2 mt-2">
              {uncertainty.flags.map((flag) => (
                <span key={flag} className="badge bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300 text-xs">
                  {flag.replace(/_/g, ' ')}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Rule Scorecard */}
      {ruleScorecard && <RuleScorecardPanel scorecard={ruleScorecard} />}

      {/* Findings */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200 mb-4">
          Findings {findings.length > 0 && `(${findings.length})`}
        </h3>
        {findings.length > 0 ? (
          <div className="space-y-3">
            {findings.map((finding, idx) => (
              <FindingCard key={idx} finding={finding} index={idx} surah={meta.surah} ayah={meta.ayah} language={language} prefetchedAudioUrl={findingAudioUrls[idx]} />
            ))}
          </div>
        ) : (
          <div className="text-center py-6">
            <div className="text-4xl mb-2">&#9989;</div>
            <p className="text-emerald-600 dark:text-emerald-400 font-medium">No issues found!</p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Your recitation sounds accurate. Try a more challenging ayah!
            </p>
          </div>
        )}
      </div>

    </div>
  );
};

export default FeedbackPanel;
